import { useEffect, useState } from "react";
import { api, API_BASE } from "../../api";
import { t } from "../../i18n";
import { SettingsPage } from "./SettingsPage";

type ApiKey = {
  id: string;
  name: string;
  prefix: string;
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
};

export function ApiKeysPage() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [name, setName] = useState("");
  const [days, setDays] = useState("90");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const baseUrl = new URL(
    API_BASE.replace(/\/api$/, "") + "/v1",
    window.location.origin,
  ).href;
  useEffect(() => {
    let active = true;
    void api<ApiKey[]>("/me/api-keys")
      .then((rows) => {
        if (active) setKeys(rows);
      })
      .catch((reason: Error) => {
        if (active) setError(reason.message);
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, []);
  const generate = async () => {
    setBusy(true);
    setError("");
    try {
      const row = await api<ApiKey & { key: string }>("/me/api-keys", {
        method: "POST",
        body: JSON.stringify({
          name,
          expiresAt: days
            ? new Date(Date.now() + Number(days) * 86_400_000).toISOString()
            : null,
        }),
      });
      setSecret(row.key);
      setCopied(false);
      setName("");
      const { key: _key, ...metadata } = row;
      setKeys((previous) => [metadata, ...previous]);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : t("Could not generate API key"),
      );
    } finally {
      setBusy(false);
    }
  };
  const revoke = async (id: string) => {
    setBusy(true);
    setError("");
    try {
      await api(`/me/api-keys/${id}`, { method: "DELETE" });
      setKeys((previous) =>
        previous.map((key) =>
          key.id === id ? { ...key, revokedAt: new Date().toISOString() } : key,
        ),
      );
      setConfirmId(null);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : t("Could not revoke API key"),
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <SettingsPage
      title={t("API keys")}
      description={t(
        "Connect external applications to your organization's models.",
      )}
    >
      <div className="max-w-3xl space-y-5">
        {error && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-300">
            {error}
          </p>
        )}
        <section className="panel space-y-4 p-5 sm:p-6">
          <h2 className="font-semibold">{t("Generate API key")}</h2>
          <p className="text-sm text-stone-600 dark:text-stone-400">
            {t(
              "Keys use your current model permissions in this organization. Usage counts toward your organization's billing and limits.",
            )}
          </p>
          {secret ? (
            <div className="space-y-3" role="status">
              <p className="text-sm">
                {t("Copy this key now. It will only be shown once.")}
              </p>
              <label className="block">
                <span className="label">{t("New API key")}</span>
                <input
                  readOnly
                  className="field font-mono"
                  value={secret}
                  onFocus={(event) => event.target.select()}
                />
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  className="button-primary min-h-11"
                  onClick={() => {
                    void navigator.clipboard
                      .writeText(secret)
                      .then(() => setCopied(true))
                      .catch(() =>
                        setError(
                          t(
                            "Could not copy. Select and copy the key manually.",
                          ),
                        ),
                      );
                  }}
                >
                  {copied ? t("Copied") : t("Copy key")}
                </button>
                <button
                  className="button min-h-11"
                  onClick={() => setSecret("")}
                >
                  {t("Done")}
                </button>
              </div>
            </div>
          ) : (
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                void generate();
              }}
            >
              <label className="block">
                <span className="label">{t("Key name")}</span>
                <input
                  required
                  maxLength={100}
                  className="field"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  autoComplete="off"
                />
              </label>
              <label className="block">
                <span className="label">{t("Expires after")}</span>
                <select
                  className="field"
                  value={days}
                  onChange={(event) => setDays(event.target.value)}
                >
                  <option value="30">{t("30 days")}</option>
                  <option value="90">{t("90 days")}</option>
                  <option value="365">{t("365 days")}</option>
                  <option value="">{t("Never")}</option>
                </select>
              </label>
              <button
                type="submit"
                className="button-primary min-h-11"
                disabled={busy || !name.trim()}
              >
                {busy ? t("Loading...") : t("Generate API key")}
              </button>
            </form>
          )}
        </section>
        <section className="panel space-y-4 p-5 sm:p-6" aria-busy={busy}>
          <h2 className="font-semibold">
            {t("Your keys in this organization")}
          </h2>
          {!keys.length && (
            <p className="text-sm text-stone-600 dark:text-stone-400">
              {busy ? t("Loading...") : t("No API keys yet.")}
            </p>
          )}
          {keys.map((key) => {
            const expired =
              key.expiresAt && new Date(key.expiresAt).getTime() <= Date.now();
            return (
              <div
                key={key.id}
                className="space-y-2 border-t border-stone-200 pt-4 dark:border-stone-800"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium break-words">{key.name}</p>
                    <p className="font-mono text-sm text-stone-600 dark:text-stone-400">
                      {key.prefix}…
                    </p>
                  </div>
                  <span className="text-sm">
                    {key.revokedAt
                      ? t("Revoked")
                      : expired
                        ? t("Expired")
                        : t("Active")}
                  </span>
                </div>
                <p className="text-sm text-stone-600 dark:text-stone-400">
                  {t("Last used")}:{" "}
                  {key.lastUsedAt
                    ? new Date(key.lastUsedAt).toLocaleString()
                    : t("Never")}{" "}
                  · {t("Expires")}:{" "}
                  {key.expiresAt
                    ? new Date(key.expiresAt).toLocaleDateString()
                    : t("Never")}
                </p>
                {!key.revokedAt &&
                  (confirmId === key.id ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm">
                        {t(
                          "Revoke this key? Applications using it will lose access.",
                        )}
                      </span>
                      <button
                        className="button min-h-11"
                        disabled={busy}
                        onClick={() => void revoke(key.id)}
                      >
                        {t("Confirm revoke")}
                      </button>
                      <button
                        className="button min-h-11"
                        disabled={busy}
                        onClick={() => setConfirmId(null)}
                      >
                        {t("Cancel")}
                      </button>
                    </div>
                  ) : (
                    <button
                      className="button min-h-11"
                      disabled={busy}
                      onClick={() => setConfirmId(key.id)}
                    >
                      {t("Revoke")}
                    </button>
                  ))}
              </div>
            );
          })}
        </section>
        <section className="panel space-y-3 p-5 sm:p-6">
          <h2 className="font-semibold">{t("Connect an application")}</h2>
          <p className="text-sm text-stone-600 dark:text-stone-400">
            {t(
              "Choose an OpenAI-compatible provider, enter this base URL, and use your generated key.",
            )}
          </p>
          <label className="block">
            <span className="label">{t("Base URL")}</span>
            <input
              className="field font-mono"
              readOnly
              value={baseUrl}
              onFocus={(event) => event.target.select()}
            />
          </label>
          <p className="text-sm text-stone-600 dark:text-stone-400">
            {t(
              "Use GET /v1/models to find model IDs. This version supports text chat completions and streaming. Tools, images, embeddings, and the Responses API are not supported yet.",
            )}
          </p>
          <pre className="overflow-x-auto rounded-lg bg-stone-950 p-4 text-xs text-stone-200">{`curl ${baseUrl}/models \\\n  -H "Authorization: Bearer $LUMEN_API_KEY"`}</pre>
        </section>
      </div>
    </SettingsPage>
  );
}
